import { isStringTrue } from "@iddataweb/idw-common-ui/utils";
import {
    OIDC_ID_TOKEN_USER_ATTRIBUTES_PROPERTY_TYPE_ID,
    OIDC_LOGINHINT_EXPIRATION_PROPERTY_TYPE_ID,
    OIDC_LOGINHINT_JWT_PROPERTY_TYPE_ID,
    OIDC_LOGIN_HINT_WTRSA_PRIVATE_KEY_PROPERTY_TYPE_ID,
    OIDC_LOGIN_HINT_WTRSA_PUBLIC_KEY_PROPERTY_TYPE_ID
} from "@constants/oidc-login-hint";
import { LoginHintSecurityMethodTypes } from "@enums/login-hint-security-methode-types";
import { LoginHintExpirationInfo, LoginHintExpirationPropertyValue, LoginHintInfo } from "@idw-types/common/login-hint-info";
import { FormattedRpServiceConfigurationProperty } from "@idw-types/data/rp-service-configuration";
import { FormattedAppliedProfile } from "@idw-types/data/applied-profile/applied-profile";
import { getPropertyValuesPassedToService, getRpServicePropertyValueOrAppliedProfilePropertyValue } from "@utils/applied-profile-util";
import { FormattedIdwProfileProperty } from "@idw-types/data/idw-profile";
import { isNullOrUndefined } from "@utils/object-util";
import { LoginHintExpirationTypeTypes } from "@enums/login-hint-expiration-type-types";
import { isValid } from "date-fns";

export const getLoginHintInfo = (
    properties: Array<FormattedRpServiceConfigurationProperty>,
    appliedProfiles: Array<FormattedAppliedProfile>
): LoginHintInfo => {
    let isLoginHintJWTEnabled = false;
    let isOidcTokenUserAttributeEnabled = false;
    let isWTRSAPrivateKeyEnabled = false;
    let isWTRSAPublicKeyEnabled = false;
    let tRSAPrivateKeyPropertyValue: string;
    let tRSAPublicKeyPropertyValue: string;

    // oidc login hint JWT property
    const oidcLoginHintJWTProperty = properties.find(property => isOIDCLoginHintJWTProperty(property.propertyTypeId) && property.addedToService);
    if (oidcLoginHintJWTProperty) {
        isLoginHintJWTEnabled = isStringTrue(oidcLoginHintJWTProperty.value);
    } else {
        const oidcLoginHintJWTProfilePropertyValue = getPropertyValuesPassedToService(appliedProfiles, OIDC_LOGINHINT_JWT_PROPERTY_TYPE_ID);
        if (oidcLoginHintJWTProfilePropertyValue) {
            isLoginHintJWTEnabled = isStringTrue(oidcLoginHintJWTProfilePropertyValue);
        }
    }

    // oidc id token user attribute property
    const oidcIdTokenUserAttributesProperty = properties.find(property => isOIDCIdTokenUserAttributeProperty(property.propertyTypeId) && property.addedToService);
    if (oidcIdTokenUserAttributesProperty) {
        isOidcTokenUserAttributeEnabled = isStringTrue(oidcIdTokenUserAttributesProperty.value);
    } else {
        const oidcIdTokenUserAttributesProfilePropertyValue = getPropertyValuesPassedToService(appliedProfiles, OIDC_ID_TOKEN_USER_ATTRIBUTES_PROPERTY_TYPE_ID);
        if (oidcIdTokenUserAttributesProfilePropertyValue) {
            isOidcTokenUserAttributeEnabled = isStringTrue(oidcIdTokenUserAttributesProfilePropertyValue);
        }
    }

    // wtrsa public key property
    const oidcLoginHintWTRSAPublicKeyProperty = properties.find(property => isWTRSAPublicKeyProperty(property.propertyTypeId) && property.addedToService);
    if (oidcLoginHintWTRSAPublicKeyProperty) {
        isWTRSAPublicKeyEnabled = true;
        tRSAPublicKeyPropertyValue = oidcLoginHintWTRSAPublicKeyProperty.value;
    } else {
        const oidcLoginHintWTRSAPublicKeyProfilePropertyValue = getPropertyValuesPassedToService(appliedProfiles, OIDC_LOGIN_HINT_WTRSA_PUBLIC_KEY_PROPERTY_TYPE_ID);
        if (oidcLoginHintWTRSAPublicKeyProfilePropertyValue !== null) {
            isWTRSAPublicKeyEnabled = true;
            tRSAPublicKeyPropertyValue = oidcLoginHintWTRSAPublicKeyProfilePropertyValue;
        }
    }

    // wtrsa private key property
    const oidcLoginHintWTRSAPrivateKeyProperty = properties.find(property => isWTRSAPrivateKeyProperty(property.propertyTypeId) && property.addedToService);
    if (oidcLoginHintWTRSAPrivateKeyProperty) {
        isWTRSAPrivateKeyEnabled = true;
        tRSAPrivateKeyPropertyValue = oidcLoginHintWTRSAPrivateKeyProperty.value;
    } else {
        const oidcLoginHintWTRSAPrivateKeyProfilePropertyValue = getPropertyValuesPassedToService(appliedProfiles, OIDC_LOGIN_HINT_WTRSA_PRIVATE_KEY_PROPERTY_TYPE_ID);
        if (oidcLoginHintWTRSAPrivateKeyProfilePropertyValue !== null) {
            isWTRSAPrivateKeyEnabled = true;
            tRSAPrivateKeyPropertyValue = oidcLoginHintWTRSAPrivateKeyProfilePropertyValue;
        }
    }

    // Expiration 
    const loginHintExpirationInfo = getLoginHintExpirationInfo(properties, appliedProfiles);

    return {
        isLoginHintJWTEnabled,
        isOidcTokenUserAttributeEnabled,
        isWTRSAPublicKeyEnabled,
        isWTRSAPrivateKeyEnabled,
        tRSAPublicKeyPropertyValue,
        tRSAPrivateKeyPropertyValue,
        ...loginHintExpirationInfo
    }
}

const getPropertyInfoByID = <T>(
    propertyTypeId: string,
    properties: Array<FormattedRpServiceConfigurationProperty>,
    appliedProfiles: Array<FormattedAppliedProfile>
): { isEnabled: boolean, value: T } => {
    const result = getRpServicePropertyValueOrAppliedProfilePropertyValue(properties, appliedProfiles, propertyTypeId);
    const isEnabled = !isNullOrUndefined(result);
    const value = result as T;

    return {
        isEnabled,
        value
    };
}

export const getLoginHintExpirationInfo = (
    properties: Array<FormattedRpServiceConfigurationProperty>,
    appliedProfiles: Array<FormattedAppliedProfile>): LoginHintExpirationInfo => {

    const oidcLoginHintExpirationProperty = getPropertyInfoByID<LoginHintExpirationPropertyValue>(OIDC_LOGINHINT_EXPIRATION_PROPERTY_TYPE_ID, properties, appliedProfiles);

    return {
        isExpireLoginHintEnabled: oidcLoginHintExpirationProperty.isEnabled,
        ...getFormattedLoginHintExpirationValue(oidcLoginHintExpirationProperty.value),
        loginHintExpirationValue: oidcLoginHintExpirationProperty.value
    }
}

export const getFormattedLoginHintExpirationValue = (loginHintPropertyValue: LoginHintExpirationPropertyValue) => {
    let loginHintDuration = null;
    let loginHintExpirationDate = null;
    if (loginHintPropertyValue?.type === LoginHintExpirationTypeTypes.DURATION && loginHintPropertyValue?.value !== null) {
        loginHintDuration = Number(loginHintPropertyValue.value) >= 0 ? (Number(loginHintPropertyValue.value) / 60000).toString() : loginHintPropertyValue.value;
    } else if (loginHintPropertyValue?.type === LoginHintExpirationTypeTypes.DATE_AND_TIME) {
        if (loginHintPropertyValue?.value !== null) {
            const date = new Date(parseInt(loginHintPropertyValue.value));
            loginHintExpirationDate = isValid(date) ? date : null;
        }
    }

    return {
        loginHintExpirationType: loginHintPropertyValue?.type || null,
        loginHintExpirationDate,
        loginHintDuration
    }
}

export const loginHintExpirationValueToMs = (loginHintExpirationType: string, loginHintExpirationValue: string): string => {
    if (loginHintExpirationType === LoginHintExpirationTypeTypes.DURATION) {
        const value = parseInt(loginHintExpirationValue);
        return Number.isNaN(value) ? null : (parseInt(loginHintExpirationValue) * 60000).toString();
    } else {
        return new Date(parseInt(loginHintExpirationValue)).getTime().toString();
    }
}

export const isOIDCLoginHintJWTProperty = (propertyTypeId: string) => {
    return propertyTypeId === OIDC_LOGINHINT_JWT_PROPERTY_TYPE_ID
}

export const isWTRSAPublicKeyProperty = (propertyTypeId: string) => {
    return propertyTypeId === OIDC_LOGIN_HINT_WTRSA_PUBLIC_KEY_PROPERTY_TYPE_ID
}

export const isWTRSAPrivateKeyProperty = (propertyTypeId: string) => {
    return propertyTypeId === OIDC_LOGIN_HINT_WTRSA_PRIVATE_KEY_PROPERTY_TYPE_ID
}

export const isOIDCIdTokenUserAttributeProperty = (propertyTypeId: string) => {
    return propertyTypeId === OIDC_ID_TOKEN_USER_ATTRIBUTES_PROPERTY_TYPE_ID
}

export const getSelectedLoginHintSecurityMethod = (
    isOidcTokenUserAttributeEnabled: boolean,
    isWTRSAPublicKeyEnabled: boolean,
    isWTRSAPrivateKeyEnabled: boolean,
    isLoginHintJWTEnabled: boolean
) => {
    if (isOidcTokenUserAttributeEnabled && isLoginHintJWTEnabled && isWTRSAPublicKeyEnabled && isWTRSAPrivateKeyEnabled) {
        return LoginHintSecurityMethodTypes.JWT_ENCRYPTED;
    } else if (isOidcTokenUserAttributeEnabled && !isLoginHintJWTEnabled && !isWTRSAPublicKeyEnabled && !isWTRSAPrivateKeyEnabled) {
        return LoginHintSecurityMethodTypes.PLAIN_JSON;
    }
    else if (isOidcTokenUserAttributeEnabled && isLoginHintJWTEnabled && !isWTRSAPublicKeyEnabled && !isWTRSAPrivateKeyEnabled) {
        return LoginHintSecurityMethodTypes.SIGNED_JWT;
    }

    return null;
}

export const setValueAndAddedToServiceForProperty = (
    property: FormattedRpServiceConfigurationProperty,
    value: string,
    addedToService: boolean
): FormattedRpServiceConfigurationProperty => {
    return { ...property, addedToService, value };
}

export const setValueAndAddedToProfileForProfileProperty = (
    property: FormattedIdwProfileProperty,
    value: string,
    addedToProfile: boolean
): FormattedIdwProfileProperty => {
    return { ...property, addedToProfile, value };
};